import { Permission, RoleDefinition, UserRole, PostStatus, AccountStatus } from "./types";
import { MODULE_PERMISSIONS, PERMISSIONS } from "./permissions";

/**
 * Build a RoleDefinition dynamically
 * @param modules - list of module names from MODULE_PERMISSIONS
 * @param extraPermissions - any extra Permission[] to include
 * @param options - optional role capabilities
 */
export function createRoleDefinition(
  modules: (keyof typeof MODULE_PERMISSIONS)[],
  extraPermissions: Permission[] = [],
  options?: {
    canSetPostStatuses?: PostStatus[];
    canSetUserAccountStatuses?: AccountStatus[];
    canAssignRoles?: UserRole[];
    canServe?: UserRole[];
  }
): RoleDefinition {
  const permissions: Permission[] = [
    ...modules.flatMap((mod) => MODULE_PERMISSIONS[mod] || []),
    ...extraPermissions,
  ];

  return {
    permissions,
    ...options,
  };
}

/**
 * Helper to get all permissions from MODULE_PERMISSIONS dynamically
 */
export function getAllPermissions(): Permission[] {
  return Object.values(MODULE_PERMISSIONS).flat();
}

/**
 * Helper to create a role with full permissions
 */
export function createFullAccessRole(options?: {
  canSetPostStatuses?: PostStatus[];
  canSetUserAccountStatuses?: AccountStatus[];
  canAssignRoles?: UserRole[];
  canServe?: UserRole[];
}): RoleDefinition {
  return createRoleDefinition(
    Object.keys(MODULE_PERMISSIONS) as (keyof typeof MODULE_PERMISSIONS)[],
    [],
    options
  );
}

/**
 * Helper to merge multiple RoleDefinitions into one
 */
export function mergeRoleDefinitions(...roles: RoleDefinition[]): RoleDefinition {
  const permissions = Array.from(new Set(roles.flatMap((r) => r.permissions)));
  const canSetPostStatuses = Array.from(
    new Set(roles.flatMap((r) => r.canSetPostStatuses || []))
  );
  const canSetUserAccountStatuses = Array.from(
    new Set(roles.flatMap((r) => r.canSetUserAccountStatuses || []))
  );
  const canAssignRoles = Array.from(
    new Set(roles.flatMap((r) => r.canAssignRoles || []))
  );
  const canServe = Array.from(
    new Set(roles.flatMap((r) => r.canServe || []))
  );

  return {
    permissions,
    canSetPostStatuses: canSetPostStatuses.length ? canSetPostStatuses : undefined,
    canSetUserAccountStatuses: canSetUserAccountStatuses.length ? canSetUserAccountStatuses : undefined,
    canAssignRoles: canAssignRoles.length ? canAssignRoles : undefined,
    canServe: canServe.length ? canServe : undefined,
  };
}


// EXAMPLES

// import { createRoleDefinition, createFullAccessRole, mergeRoleDefinitions } from "./role.helpers";
// import { PERMISSIONS } from "./permissions";

// // Simple custom role for matchmaking moderators
// const matchmakingModRole = createRoleDefinition(
//   ["profiles", "reactions", "conversations"],
//   [PERMISSIONS.BOOKING_ALLVIEW],
//   {
//     canServe: ["user"],
//     canSetPostStatuses: ["review"],
//   }
// );

// // Full access admin role
// const superAdminRole = createFullAccessRole({
//   canAssignRoles: ["guest", "user", "writer", "reviewer", "editor", "admin"],
//   canSetUserAccountStatuses: ["pending", "verified", "suspended", "deleted"],
// });

// // Merge two roles into one
// const combinedRole = mergeRoleDefinitions(matchmakingModRole, superAdminRole);

// Add or remove modules easily (["profiles", "messages"]).
// 	2.	Include extra permissions dynamically (extraPermissions).
// 	3.	Assign granular capabilities (canServe, canAssignRoles, etc.).
// 	4.	Merge multiple role definitions without duplicates.
// 	5.	Fully type-safe, works with your PERMISSIONS constants.